import { useState, useEffect, useContext } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { GlobalContext } from "context/Provider";
import Session from "helpers/session";
import API from "api";
import {
  StyledContainer,
  TabBar,
  TabButton,
  SectionBox,
  SectionTitle,
  FieldLabel,
  FieldNote,
  StyledInput,
  StyledTextarea,
  StyledGrid,
  StyledSelectionGrid,
  ToggleRow,
  StyledDivider,
  SubmitButton,
  AddOrderButton,
  OptionalBadge,
} from "./NewOrder.styles";
import {RowInterface} from "../../../../context/initialState/Provider";
import DeviceSelector from "./NewOrder.utils";
import {
  OrderStatusAlert,
  BLUETOOTH_DEVICES,
  CELLULAR_DEVICES
} from "./NewOrder.utils";
import { isWholeNumber } from "../../../../helpers/variables";
import {individualOrderInterface, bulkOrderInterface} from "../../../../api/kore/orders";
import { useNavigate } from "react-router-dom";

interface Device {
  id: string;
  serialnumber: string;
  type: string;
  model?: string;
  assigned: boolean;
  user?: string;
  createdAt: string;
}

interface ShippingData {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface Order {
  _id: number;
  open: boolean;
  memberId: string;
  firstName: string;
  lastName: string;
  email: string;
  dob: string;
  shipping: ShippingData;
  hasHub: boolean;
  selectedHub: string;
  btDevices: string[];
  celDevices: string[];
}

interface BulkOrder {
  kit: {
    devices: string[];
    qty: string;
    includeGateway: boolean;
  };
  devices: {
    quantities: { [name: string]: string };
    gatewayQty: string;
  };
  shipping: ShippingData;
  notes: string;
}

const emptyShipping = (): ShippingData => ({
  street: "", city: "", state: "", zip: "", country: "",
});

let nextId = 1;
const newOrder = (): Order => ({
  _id: nextId++,
  open: true,
  memberId: "", firstName: "", lastName: "", email: "", dob: "",
  shipping: emptyShipping(),
  hasHub: false, selectedHub: "",
  btDevices: [], celDevices: [],
});


const newBulkOrder = (): BulkOrder => ({
  kit: {
    devices: [],
    qty: "",
    includeGateway: false,
  },
  devices: {
    quantities: {},
    gatewayQty: "",
  },
  shipping: emptyShipping(),
  notes: "",
});

export const Field = ({ label, optional, children, note }: {
  label: string; optional?: boolean; children: React.ReactNode; note?: string;
}) => (
  <Box sx={{ mb: 2 }}>
    <FieldLabel>
      {label}
      {optional && <OptionalBadge>optional</OptionalBadge>}
    </FieldLabel>
    {children}
    {note && <FieldNote>{note}</FieldNote>}
  </Box>
);

const ShippingFields = ({ data, onChange, err }: {
  data: ShippingData;
  onChange: (key: string, val: string) => void;
  err?: (val: string) => object;
}) => (
  <>
    <SectionTitle sx={{ mb: 1.5 }}>Shipping address</SectionTitle>
    <Field label="Street address">
      <StyledInput
        style={err?.("street")}
        value={data.street}
        onChange={(event: any) => onChange("street", event.target.value)}
        placeholder="123 Main St"
      />
    </Field>
    <StyledGrid>
      <Field label="City">
        <StyledInput
          style={err?.("city")}
          value={data.city}
          onChange={(event: any) => onChange("city", event.target.value)}
        />
      </Field>
      <Field label="State">
        <StyledInput
          style={err?.("state")}
          value={data.state}
          onChange={(event: any) => onChange("state", event.target.value)} placeholder="CA"
        />
      </Field>
    </StyledGrid>
    <StyledGrid>
      <Field label="ZIP code">
        <StyledInput
          style={err?.("zip")}
          value={data.zip}
          onChange={(event: any) => onChange("zip", event.target.value)} placeholder="90210"
        />
      </Field>
      <Field label="Country">
        <StyledInput
          style={err?.("country")}
          value={data.country}
          onChange={(event: any) => onChange("country", event.target.value)} placeholder="US"
        />
      </Field>
    </StyledGrid>
  </>
);

const IndividualOrderCard = ({ order, onChange, onRemove, providerEmail, devices, submitted }: {
  order: Order;
  onChange: (o: Order) => void;
  onRemove: (() => void) | null;
  providerEmail: string;
  devices: Device[];
  submitted: boolean;
}) => {
  const { provider } = useContext(GlobalContext);
  const open = order.open;
  const err =
    (field: string) => submitted && !order.shipping[field as keyof ShippingData]
    ? { border: "1px solid red" }
    : {};

  const setField = (key: keyof Order, val: any) => onChange({ ...order, [key]: val });
  const setShipping = (key: string, val: string) =>
    onChange({ ...order, shipping: { ...order.shipping, [key]: val } });

  const handleMemberSelect = (id: string) => {
    const member = provider?.memberList.find((targetMember: any) => targetMember.id === id) as any;
    if (!member) { onChange({ ...order, memberId: "" }); return; }
    onChange({
      ...order,
      memberId:    member.id,
      firstName:   member.firstName,
      lastName:    member.lastName,
      email:       member.email || "",
      dob:         member.birthDate || "",
      hasHub: !!(member.hubIds?.length),
      selectedHub: member.hubIds?.[0] || "",
    });
  };

  const toggleDevice = (key: "btDevices" | "celDevices", item: string) => {
    const currentDevice = order[key];
    onChange({
      ...order,
      [key]: currentDevice.includes(item) ? currentDevice.filter(x => x !== item) : [...currentDevice, item]
    });
  };

  const label = order.firstName
    ? `${order.firstName} ${order.lastName}`.trim()
    : `New order`;

  return (
    <Accordion
      expanded={open}
      onChange={() => setField("open", !open)}
      sx={{
        mb: 1,
        mt: 1,
        "&.Mui-expanded": {
          margin: "8px 0 !important",
        }
      }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>{label}</Typography>
        {onRemove && (
          <Typography
            variant="caption"
            color="error"
            sx={{ cursor: "pointer", mr: 2 }}
            onClick={error => { error.stopPropagation(); onRemove(); }}
          >
            Remove
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <SectionBox>
          <SectionTitle>Patient information</SectionTitle>
          <Field label="Select patient">
            <Autocomplete
              options={provider?.memberList ?? []}
              getOptionLabel={(member: RowInterface) => `${member.firstName} ${member.lastName}`}
              value={provider?.memberList.find(member => member.id === order.memberId) ?? null}
              onChange={(_, selected) => {
                if (selected) handleMemberSelect(selected.id);
                else onChange({ ...order, memberId: "" });
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search patients…" size="small" />
              )}
            />
          </Field>
          <FieldNote sx={{ mb: 2 }}>
            Fields below are pre-filled from the patient record but can be edited before submitting.
          </FieldNote>
          <StyledDivider />
          <StyledGrid>
            <Field label="First name">
              <StyledInput
                style={err(order.firstName)}
                value={order.firstName}
                onChange={(event: any) => setField("firstName", event.target.value)}
                placeholder="John"
              />
            </Field>
            <Field label="Last name">
              <StyledInput
                style={err(order.lastName)}
                value={order.lastName}
                onChange={(event: any) => setField("lastName", event.target.value)}
                placeholder="Smith"
              />
            </Field>
          </StyledGrid>
          <StyledGrid>
            <Field label="Member ID">
              <StyledInput
                style={err(order.memberId)}
                value={order.memberId}
                onChange={(event: any) => setField("memberId", event.target.value)}
                placeholder="xxxxxxxxxxxxxxxxxxxxxxxx"
              />
            </Field>
            <Field label="Date of birth">
              <StyledInput
                style={err(order.dob)}
                value={order.dob}
                onChange={(event: any) => setField("dob", event.target.value)}
                placeholder="YYYY-MM-DD"
              />
            </Field>
          </StyledGrid>
          <Field label="Email address" optional>
            <StyledInput
              value={order.email}
              onChange={(event: any) => setField("email", event.target.value)}
              placeholder="patient@email.com"
            />
          </Field>
        </SectionBox>
        <SectionBox>
          <ShippingFields data={order.shipping} onChange={setShipping} err={err}/>
        </SectionBox>
        <SectionBox>
          <SectionTitle>Gateway / hub</SectionTitle>
          {submitted && order.hasHub && !order.selectedHub && (
            <FieldNote sx={{ color: "error.main", mb: 1 }}>
              Please select a hub for this patient.
            </FieldNote>
          )}
          <ToggleRow>
            <FormControlLabel
              control={
                <Checkbox
                  checked={order.hasHub}
                  onChange={(event: any) =>
                    onChange({ ...order, hasHub: event.target.checked, selectedHub: "" })}
                  size="small"
                />
              }
              label="Patient already has a hub"
            />
          </ToggleRow>
          {order.hasHub ? (
            <Field label="Select hub">
              <Autocomplete
                options={devices}
                getOptionLabel={(device: Device) =>
                  device.model ? `${device.serialnumber} — ${device.model}` : device.serialnumber
                }
                value={devices.find(device => device.serialnumber === order.selectedHub) ?? null}
                onChange={(_, selected) => {
                  setField("selectedHub", selected?.serialnumber ?? "");
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search hubs…" size="small" />
                )}
              />
            </Field>
          ) : (
            <FieldNote>A new hub/gateway will be included with this order.</FieldNote>
          )}
        </SectionBox>
        <SectionBox>
          <SectionTitle>Devices</SectionTitle>
          {submitted && order.btDevices.length === 0 && order.celDevices.length === 0 && (
            <FieldNote sx={{ color: "error.main", mb: 1 }}>
              Please select at least one device.
            </FieldNote>
          )}
          <DeviceSelector
            btDevices={order.btDevices}
            celDevices={order.celDevices}
            onToggle={toggleDevice}
          />
        </SectionBox>
        <FieldNote>
          Order updates will be sent to <strong>{providerEmail}</strong> (your account email).
        </FieldNote>
      </AccordionDetails>
    </Accordion>
  );
};

const IndividualTab = ({ providerEmail, providerName, providerOrganization, devices }: {
  providerEmail: string;
  providerName: string;
  providerOrganization: string;
  devices: Device[]
}) => {
  const [orders, setOrders] = useState<Order[]>([newOrder()]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"success" | "error" | "validationError" | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const updateOrder = (idx: number, updated: Order) =>
    setOrders(prev => prev.map((o, i) => i === idx ? updated : o));
  const removeOrder = (idx: number) =>
    setOrders(prev => prev.filter((_, i) => i !== idx));
  const addOrder = () => setOrders(prev => [...prev, newOrder()]);

  const validateOrder = (order: Order): string[] => {
    const missing = [];
    if (!order.firstName) missing.push("First name");
    if (!order.lastName) missing.push("Last name");
    if (!order.memberId) missing.push("Member ID");
    if (!order.dob) missing.push("Date of birth");
    if (!order.shipping.street) missing.push("Street address");
    if (!order.shipping.city) missing.push("City");
    if (!order.shipping.state) missing.push("State");
    if (!order.shipping.zip) missing.push("ZIP code");
    if (!order.shipping.country) missing.push("Country");
    if (order.hasHub && !order.selectedHub) missing.push("hub");
    if (order.btDevices.length === 0 && order.celDevices.length === 0) missing.push("devices");
    return missing;
  };
  const handleSubmit = () => {
    setSubmitted(true);
    const allValid = orders.every(order => validateOrder(order).length === 0);
    if (!allValid) {
      setStatus("validationError");
      return;
    }
    setLoading(true);
    setStatus(null);

    const payload: individualOrderInterface = {
      type: "individual",
      delivery: "Ground",
      issuedBy: providerName ?? "",
      issuedEmail: providerEmail ?? "",
      organization: providerOrganization ?? "",
      providerEmail: providerEmail ?? "",
      orders: orders.map(({ _id, open, ...o }) => o),
    };

    API.kore.orders.createIndividual(payload, Session.crackCookieToken("provider"))
      .then(() => {
        setStatus("success");
        navigate("/provider/orders");
      })
      .catch(() => setStatus("error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!status) return;
    const timer = setTimeout(() => setStatus(null), 4000);
    return () => clearTimeout(timer);
  }, [status]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {orders.map((order, index) => (
        <IndividualOrderCard
          key={order._id}
          order={order}
          onChange={updated => updateOrder(index, updated)}
          onRemove={orders.length > 1 ? () => removeOrder(index) : null}
          providerEmail={providerEmail}
          devices={devices}
          submitted={submitted}
        />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, mt: 1, pl: 3, pt: 1 }}>
        <AddOrderButton onClick={addOrder} disabled={loading}>+ Add another person</AddOrderButton>
        <SubmitButton onClick={handleSubmit} disabled={loading}>Submit orders</SubmitButton>
        {status && ( <OrderStatusAlert status={status} /> )}
      </Box>
    </Box>
  );
};

const BulkTab = ({ providerEmail, providerName, providerOrganization }: {
  providerEmail: string;
  providerName: string;
  providerOrganization: string;
}) => {
  const [order, setOrder] = useState<BulkOrder>(newBulkOrder());
  const [loading, setLoading]   = useState(false);
  const [status, setStatus]     = useState<"success" | "error" | "validationError" | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const setField =
    (key: keyof BulkOrder, val: any) => setOrder(order => ({ ...order, [key]: val }));
  const setAddr =
    (key: string, value: string) => setOrder(order => ({ ...order, shipping: { ...order.shipping, [key]: value } }));
  const toggleKitDevice = (key: "btDevices" | "celDevices", item: string) =>
    setOrder(order => ({
      ...order,
      kit: {
        ...order.kit,
        devices: order.kit.devices.includes(item)
          ? order.kit.devices.filter(targetDevice => targetDevice !== item)
          : [...order.kit.devices, item]
      }
    }));

  const handleSubmit = () => {
    setSubmitted(true);
    const errors = validateBulkOrder(order);
    const hasErrors = errors.kits || errors.devices || errors.shipping.length > 0;
    if (hasErrors) {
      setStatus("validationError");
      return;
    }
    setLoading(true);
    setStatus(null);
    const payload: bulkOrderInterface = {
      type: "bulk",
      delivery: "Ground",
      issuedBy: providerName ?? "",
      issuedEmail: providerEmail ?? "",
      organization: providerOrganization ?? "",
      providerEmail: providerEmail ?? "",
      ...order,
    };

    API.kore.orders.createBulk(payload, Session.crackCookieToken("provider"))
      .then(() => {
        setStatus("success");
        navigate("/provider/orders");
      })
      .catch(() => setStatus("error"))
      .finally(() => setLoading(false));
  };

  const validateBulkOrder = (order: BulkOrder): { kits: string; devices: string; shipping: string[] } => {
    const errors = { kits: "", devices: "", shipping: [] as string[] };

    const hasKitDevices = order.kit.devices.length > 0;
    if (hasKitDevices && !order.kit.qty) errors.kits = "Please enter a kit quantity.";
    if (order.kit.qty && !hasKitDevices) errors.kits = "Please select at least one device for the kit.";

    const hasDeviceQtys = Object.values(order.devices.quantities).some(v => v && parseInt(v) >= 1);
    const bothEmpty = !hasKitDevices && !hasDeviceQtys && !order.devices.gatewayQty;
    if (bothEmpty) {
      errors.kits = errors.kits || "Please complete at least one of the kits or devices sections.";
      errors.devices = "Please complete at least one of the kits or devices sections.";
    }

    if (!order.shipping.street) errors.shipping.push("street");
    if (!order.shipping.city) errors.shipping.push("city");
    if (!order.shipping.state) errors.shipping.push("state");
    if (!order.shipping.zip) errors.shipping.push("zip");
    if (!order.shipping.country) errors.shipping.push("country");

    return errors;
  };

  const err = (field: string) => submitted && validateBulkOrder(order).shipping.includes(field)
    ? { border: "1px solid red" }
    : {};

  useEffect(() => {
    if (!status) return;
    const timer = setTimeout(() => setStatus(null), 4000);
    return () => clearTimeout(timer);
  }, [status]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ flex: 1, padding: 2, overflowY: "auto" }}>
        <SectionBox>
          <SectionTitle>Kits</SectionTitle>
          {submitted && validateBulkOrder(order).kits && (
            <FieldNote sx={{ color: "error.main", mb: 1 }}>
              {validateBulkOrder(order).kits}
            </FieldNote>
          )}
          <FieldNote sx={{ mb: 2 }}>
            Kits include a curated set of devices prepared and whitelisted for the patient. Select the accessories to include and specify a quantity.
          </FieldNote>
          <DeviceSelector
            btDevices={order.kit.devices.filter(targetDevice => BLUETOOTH_DEVICES.includes(targetDevice))}
            celDevices={order.kit.devices.filter(targetDevice => CELLULAR_DEVICES.includes(targetDevice))}
            onToggle={toggleKitDevice}
          />
          <StyledGrid sx={{ mt: 2 }}>
            <Field label="Kit quantity">
              <StyledInput
                value={order.kit.qty}
                style={{ width: 70 }}
                onChange={(event: any) =>
                  setOrder(order => ({ ...order, kit: { ...order.kit, qty: event.target.value } }))
                }
                placeholder="0"
              />
            </Field>
          </StyledGrid>
          <FormControlLabel
            control={
              <Checkbox
                checked={order.kit.includeGateway}
                onChange={(event: any) =>
                  setOrder(order => ({ ...order, kit: { ...order.kit, includeGateway: event.target.checked } }))
                }
                size="small"
              />
            }
            label="Include gateway with each kit"
          />
        </SectionBox>
        <SectionBox>
          <SectionTitle>Devices</SectionTitle>
          {submitted && validateBulkOrder(order).devices && (
            <FieldNote sx={{ color: "error.main", mb: 1 }}>
              {validateBulkOrder(order).devices}
            </FieldNote>
          )}
          <FieldNote sx={{ mb: 2 }}>
            Order individual devices by quantity. Only devices with a quantity entered will be included in the order.
          </FieldNote>
          <FieldLabel>Bluetooth devices</FieldLabel>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            <StyledSelectionGrid sx={{ mb: 2 }}>
              {BLUETOOTH_DEVICES.map(device => (
                <Box key={device} sx={{ display: "flex", alignItems: "center", gap: 1, maxWidth: 350 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>{device}</Typography>
                  <StyledInput
                    style={{ width: 64 }}
                    placeholder="0"
                    value={order.devices.quantities[device] || ""}
                    onChange={(event: any) => {
                      const val = event.target.value;
                      if (!isWholeNumber(val)) return;
                      setOrder(order => ({
                        ...order,
                        devices: {
                          ...order.devices,
                          quantities: val
                            ? { ...order.devices.quantities, [device]: val }
                            : Object.fromEntries(Object.entries(order.devices.quantities).filter(([targetDevice]) =>
                              targetDevice !== device))
                        }
                      }));

                    }}
                  />
                </Box>
              ))}
            </StyledSelectionGrid>
          </Box>
          <FieldLabel>Cellular devices</FieldLabel>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            <StyledSelectionGrid sx={{ mb: 2 }}>
              {CELLULAR_DEVICES.map(device => (
                <Box key={device} sx={{ display: "flex", alignItems: "center", gap: 1, maxWidth: 350 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>{device}</Typography>
                  <StyledInput
                    style={{ width: 64 }}
                    placeholder="0"
                    value={order.devices.quantities[device] || ""}
                    onChange={(event: any) => {
                      const val = event.target.value;
                      if (!isWholeNumber(val)) return;
                      setOrder(order => ({
                        ...order,
                        devices: {
                          ...order.devices,
                          quantities: val
                            ? { ...order.devices.quantities, [device]: val }
                            : Object.fromEntries(Object.entries(order.devices.quantities).filter(([targetDevice]) =>
                              targetDevice !== device))
                        }
                      }));
                    }}
                  />
                </Box>
              ))}
            </StyledSelectionGrid>
          </Box>
          <Field label="Gateway quantity">
            <StyledInput
              value={order.devices.gatewayQty}
              style={{ width: 70 }}
              onChange={(event: any) =>
                setOrder(order => ({ ...order, devices: { ...order.devices, gatewayQty: event.target.value } }))
              }
              placeholder="0"
            />
          </Field>
        </SectionBox>
        <SectionBox>
          <ShippingFields data={order.shipping} onChange={setAddr} err={err}/>
        </SectionBox>
        <SectionBox>
          <Field label="Notes" optional>
            <StyledTextarea
              value={order.notes}
              onChange={(event: any) => setField("notes", event.target.value)}
              placeholder="Any special instructions for this order…"
            />
          </Field>
        </SectionBox>
        <FieldNote sx={{ mb: 2 }}>
          Order updates will be sent to <strong>{providerEmail}</strong> (your account email).
        </FieldNote>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, mt: 1, pl: 3, pt: 1 }}>
        <SubmitButton onClick={handleSubmit} disabled={loading}>Submit bulk order</SubmitButton>
        <OrderStatusAlert status={status} />
      </Box>
    </Box>
  );
};

export default function NewOrder() {
  const { provider } = useContext(GlobalContext);
  const [tab, setTab] = useState<"individual" | "bulk">("individual");
  const [devices, setDevices] = useState<Device[]>([]);

  useEffect(() => {
    const token = Session.crackCookieToken("provider");
    API.kore.devices.getAll(token, "500").then((res: any) => {
      const list = res?.items ?? [];
      setDevices(list);
    });
  }, []);

  const providerEmail = provider?.loggedInClient?.email ?? "";
  const providerName = provider?.loggedInClient?.username ?? "";
  const providerOrganization = provider?.loggedInClient?.organization ?? "";

  return (
    <StyledContainer>
      <TabBar>
        {([ ["individual", "Individual orders"], ["bulk", "Bulk orders"] ] as const).map(([id, label]) => (
          <TabButton key={id} $active={tab === id} onClick={() => setTab(id)}>{label}</TabButton>
        ))}
      </TabBar>
      {tab === "individual"
        ? <IndividualTab
          providerEmail={providerEmail}
          providerName={providerName}
          providerOrganization={providerOrganization}
          devices={devices}
        />
        : <BulkTab
          providerEmail={providerEmail}
          providerName={providerName}
          providerOrganization={providerOrganization}
        />
      }
    </StyledContainer>
  );
}